import type { CityConfig } from "@/pages/city-template/types";

export const chathamConfig: CityConfig = {
  city: "Chatham",
  state: "New York",
  stateAbbr: "NY",
  slug: "chatham",
  route: "/cities/chatham",
  title: "Emergency & Expedited Passport Chatham NY | Same Day Passport Chatham",
  metaDescription:
    "Emergency passport Chatham NY — same day, 24-hour, and 3-5 day expedited passport processing for Columbia County. Fast Passport Center serves Chatham, Ghent, Kinderhook, Philmont, and the Hudson Valley near the Massachusetts border.",
  h1: "Emergency & Expedited Passport Services in Chatham, NY",
  heroSubtitle:
    "Chatham's fast passport solution — from the historic Columbia County village near the Massachusetts border to the rolling farmland of the Hudson Valley. Whether you're flying out of Albany International or Stewart, Fast Passport Center delivers emergency passports in as little as 24 hours and expedited processing in 3-5 days.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Historic%20village%20of%20Chatham%20New%20York%20with%20charming%20Main%20Street%20buildings%2C%20Columbia%20County%20courthouse%20clock%20tower%2C%20Taconic%20State%20Parkway%20nearby%2C%20rolling%20farmland%20with%20green%20meadows%20and%20distant%20Berkshire%20Hills%2C%20warm%20golden%20afternoon%20light%2C%20modern%20travel%20photography%20with%20warm%20earth%20tones%2C%20no%20text%20overlay%2C%20scenic%20upstate%20New%20York%20village%20with%20historic%20architecture%20and%20pastoral%20countryside%20backdrop&width=1600&height=700&seq=ny-chatham-hero-001&orientation=landscape",
  heroImageAlt: "Historic village of Chatham New York with Main Street, Columbia County courthouse, and rolling farmland at golden hour",
  geoPosition: "42.3642; -73.5948",
  geoPlacename: "Chatham, New York, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~115 miles (South in Manhattan)",
  },
  nearbyCitiesZips: [
    { name: "Chatham", zips: "12037" },
    { name: "Ghent", zips: "12075" },
    { name: "Philmont", zips: "12565" },
    { name: "East Chatham", zips: "12060" },
    { name: "Old Chatham", zips: "12136" },
    { name: "Malden Bridge", zips: "12115" },
    { name: "Nassau", zips: "12123" },
    { name: "Valatie", zips: "12184" },
    { name: "Kinderhook", zips: "12106" },
    { name: "Stuyvesant", zips: "12173" },
    { name: "Austerlitz", zips: "12017" },
    { name: "New Lebanon", zips: "12125" },
    { name: "Canaan", zips: "12029" },
    { name: "Hudson", zips: "12534" },
    { name: "Claverack", zips: "12513" },
    { name: "Athens", zips: "12015" },
    { name: "Cairo", zips: "12413" },
    { name: "Catskill", zips: "12414" },
    { name: "Albany", zips: "12201-12260" },
    { name: "Rensselaer", zips: "12144" },
    { name: "Troy", zips: "12180, 12181, 12182, 12183" },
    { name: "Pittsfield", zips: "01201, 01202, 01203" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency passport processing for Chatham travelers with urgent departures — same day service available for documented emergencies.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with direct access to the New York Passport Agency.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround. Available for Chatham residents 7 days a week.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Columbia County & MA Border",
      desc: "Chatham, Ghent, Kinderhook, Philmont, and the entire Columbia County region near the Massachusetts border.",
    },
  ],
  testimonials: [
    {
      name: "Patricia W.",
      location: "Chatham, NY",
      rating: 5,
      text: "Had a last-minute trip to Boston for a family emergency. Fast Passport Center processed my expedited passport in 4 days. Their agent was incredibly patient and walked me through everything.",
      service: "Expedited Passport",
    },
    {
      name: "Thomas B.",
      location: "Ghent, NY",
      rating: 5,
      text: "Renewed my passport for a business trip to London. Chose the 5-day option and had it back in 4. They even helped me find the best photo location near Kinderhook.",
      service: "Passport Renewal",
    },
    {
      name: "Eleanor M.",
      location: "Philmont, NY",
      rating: 5,
      text: "First-time passport for my grandson's school trip to Quebec. The agent explained the two-parent rule and helped us get the consent forms right. Processed in 5 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "chatham-1",
      name: "Chatham Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "31 Main Street",
      city: "Chatham",
      state: "NY",
      zip: "12037",
      phone: "518-392-2221",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Chatham",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main Chatham passport acceptance facility. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "chatham-2",
      name: "Kinderhook Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "3 Hudson Street",
      city: "Kinderhook",
      state: "NY",
      zip: "12106",
      phone: "518-758-6114",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Kinderhook (~8 miles west)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available. Convenient for Kinderhook, Stuyvesant, and west Columbia County residents.",
      icon: "ri-building-2-line",
    },
    {
      id: "chatham-3",
      name: "Hudson Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "365 Fairview Avenue",
      city: "Hudson",
      state: "NY",
      zip: "12534",
      phone: "518-828-1575",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hudson (~12 miles west)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available. Serving Hudson, Claverack, and the Hudson River corridor.",
      icon: "ri-building-2-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Chatham, NY?",
      a: "Fast Passport Center offers same-day emergency passport processing in as little as 24 hours for Chatham residents with documented life-or-death emergencies. For urgent travel within 2 weeks, our 3-5 day expedited service is the most popular option. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Where is the closest passport acceptance facility in Chatham?",
      a: "The Chatham Post Office at 31 Main Street is the main Chatham passport acceptance facility. The Kinderhook Post Office at 3 Hudson Street and Hudson Post Office at 365 Fairview Avenue are also convenient options. First-time applicants and those not eligible for mail-in renewal must apply in person.",
    },
    {
      q: "Can I get same-day passport photos in Chatham?",
      a: "Yes — passport photos are widely available at CVS Pharmacy, Walgreens, and Rite Aid locations in Chatham, Kinderhook, and Hudson. We also offer photo review as part of our service to ensure your 2x2 photos meet all State Department requirements.",
    },
    {
      q: "How much does an expedited passport cost in Chatham?",
      a: "Government fees are set by the State Department and vary based on passport type (book vs card, large book option). Fast Passport Center's service fees depend on processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote based on your Chatham passport needs.",
    },
    {
      q: "Do I need to visit the New York Passport Agency in person?",
      a: "No — if you use Fast Passport Center, you do not need to visit the New York Passport Agency in Manhattan. As a registered courier, we handle the agency submission for you. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "What is the fastest way to renew a passport in Chatham?",
      a: "The fastest way to renew a passport in Chatham is through Fast Passport Center's 3-5 day expedited service. If your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged, you may qualify for renewal without an acceptance clerk visit. For even faster renewal, our 24-hour emergency service is available.",
    },
    {
      q: "Can I get a child passport expedited in Chatham, NY?",
      a: "Yes — Fast Passport Center expedites child passports for minors under 16 in Chatham and all Columbia County communities. Both parents must appear with the child or provide notarized consent (Form DS-3053). We review all parental consent documentation before submission to avoid delays.",
    },
    {
      q: "Does Fast Passport Center have an office in Chatham?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Chatham. We serve Columbia County and the Hudson Valley near the Massachusetts border through our secure overnight shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Chatham / Hudson", address: "various CVS locations in Chatham, Kinderhook, and Hudson", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Albany", address: "various Walgreens locations in Albany and Troy", hours: "Mon-Sun, hours vary by location" },
    { name: "Rite Aid — Chatham", address: "Rite Aid locations in Chatham and nearby Columbia County towns", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Albany", address: "various UPS Store locations in Albany", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Albany", address: "various FedEx Office locations in Albany", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
  ],
  dmvLocations: [
    {
      name: "NYS DMV — Hudson",
      address: "560 Warren Street, Hudson, NY 12534",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Hudson DMV office for license renewals and REAL ID applications. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "NYS DMV — Albany",
      address: "224-260 Washington Avenue Extension, Albany, NY 12203",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Albany DMV office serving Columbia County and Capital Region residents.",
      featured: false,
    },
    {
      name: "NYS DMV — Catskill",
      address: "411 West Bridge Street, Catskill, NY 12414",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Catskill DMV office serving Greene County and west Columbia County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~40 miles from Chatham",
      address: "737 Albany Shaker Road, Albany, NY 12211",
      note: "Albany International is the closest major airport to Chatham with flights to major U.S. and international destinations. Perfect for Columbia County travelers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany Airport",
      featured: true,
    },
    {
      code: "SWF",
      name: "Stewart International Airport",
      distance: "~55 miles from Chatham",
      address: "1180 First Street, New Windsor, NY 12553",
      note: "Stewart International is an alternative for Chatham travelers with flights to major U.S. hubs including Orlando and Las Vegas.",
      flightUrl: "https://www.swfny.com/",
      flightLabel: "Stewart Airport",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~65 miles from Chatham (in Hartford, CT)",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and international flights. Convenient for Chatham travelers near the Massachusetts border.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Chatham travelers with urgent departures within 24 hours to 2 weeks. Direct New York Passport Agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Chatham residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Chatham residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Chatham residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Chatham, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Chatham residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Chatham travelers with immediate departure needs from Albany International or Stewart Airport." },
    { name: "Passport Renewal", description: "Fast passport renewal for Chatham residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Chatham residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Chatham residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Chatham and Columbia County." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Chatham." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Chatham travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Chatham travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Chatham team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application directly to the New York Passport Agency.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Chatham address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "New York City", route: "/cities/new-york-city" },
    { name: "Brooklyn", route: "/cities/brooklyn" },
    { name: "Queens", route: "/cities/queens" },
    { name: "Buffalo", route: "/cities/buffalo" },
    { name: "Albany", route: "/cities/albany-ny" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Syracuse", route: "/cities/syracuse" },
    { name: "Yonkers", route: "/cities/yonkers" },
    { name: "Utica", route: "/cities/utica" },
    { name: "Schenectady", route: "/cities/schenectady" },
    { name: "Troy", route: "/cities/troy" },
    { name: "Saratoga Springs", route: "/cities/saratoga-springs" },
    { name: "Elmira", route: "/cities/elmira" },
    { name: "Ithaca", route: "/cities/ithaca" },
    { name: "Corning", route: "/cities/corning" },
    { name: "Jamestown", route: "/cities/jamestown" },
    { name: "Plattsburgh", route: "/cities/plattsburgh" },
    { name: "Watertown", route: "/cities/watertown" },
    { name: "Ogdensburg", route: "/cities/ogdensburg" },
    { name: "Glens Falls", route: "/cities/glens-falls" },
    { name: "Lake George", route: "/cities/lake-george" },
    { name: "Kingston", route: "/cities/kingston" },
    { name: "Middletown", route: "/cities/middletown-ny" },
    { name: "New Paltz", route: "/cities/new-paltz" },
    { name: "Poughkeepsie", route: "/cities/poughkeepsie" },
    { name: "Newburgh", route: "/cities/newburgh" },
    { name: "Beacon", route: "/cities/beacon" },
    { name: "Fishkill", route: "/cities/fishkill" },
    { name: "Mount Vernon", route: "/cities/mount-vernon" },
    { name: "Tarrytown", route: "/cities/tarrytown" },
    { name: "Hyde Park", route: "/cities/hyde-park" },
    { name: "Red Hook", route: "/cities/red-hook" },
    { name: "Rhinebeck", route: "/cities/rhinebeck" },
    { name: "Hudson", route: "/cities/hudson-ny" },
    { name: "Catskill", route: "/cities/catskill" },
    { name: "Gloversville", route: "/cities/gloversville" },
    { name: "Amsterdam", route: "/cities/amsterdam-ny" },
    { name: "New York State", route: "/states/new-york" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Chatham and all of Columbia County as a remote courier service. We do not have a physical office in Chatham, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Chatham area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to Chatham and all of Columbia County. From the historic village near the Massachusetts border to the rural farmland of Ghent and Philmont — we serve the entire Chatham region with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Chatham and all surrounding communities across Columbia County. Whether you're in Ghent, Philmont, East Chatham, or Kinderhook, our expedited passport services are available to you.",
  howItWorksIntro:
    "Our Chatham customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Chatham travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Chatham travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about emergency and expedited passport services in Chatham. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/new-york",
  statePageName: "New York Passport Services",
};

export default chathamConfig;